import { useRouter } from 'next/router'
import SEOTitle from '@chegg-wt/core/components/SEOTitle'
import MetaDescription from '@chegg-wt/core/components/Head/MetaDescription'
import SourceSearch, {
  SourceSearchPageStructureTop,
} from '@chegg-wt/core/components/Layouts/SourceSearch'
import { getLvpLayout } from '@chegg-wt/core/components/Layouts/LargeVideoPlayerLayout'
import { NextPageWithLayout } from '@chegg-wt/core/types/layout'
import { NextPageContext } from 'next'
import { getActualStyleId, isStyleIdInValid } from '@chegg-wt/core/utils/styles'
import { getSourceTypeName, sourceTypeById } from '@chegg-wt/core/utils/sourceTypes'
import { getUrlStyleNameV2 } from '@chegg-wt/core/api/oneGraph'
import { Config } from '@chegg-wt/core/config'
import { APA_ANNOTATED_BIBLIOGRAPHY } from '@chegg-wt/core/constants/styles'
import { useOnUnloadTimeOnPage } from '@chegg/time-on-page'
import { matchSourceSegment } from '../../utils/routes'
import { upgradePromotionCopy } from '../../copy/upgrade'
import { getSeoCopyWithSource } from '../../utils/seoCopy'

interface SourceSearchPageProps {
  initialUrlStyleName: string
  styleFullName: string
}
const SourceSearchPage: NextPageWithLayout<SourceSearchPageProps> = ({
  initialUrlStyleName,
  styleFullName,
}) => {
  const router = useRouter()
  const { style, source: citeASource } = router.query as { source: string; style: string }
  let {
    params: { source },
  } = matchSourceSegment(citeASource)

  source = typeof source === 'string' ? source : ''
  const formattedSource = getSourceTypeName(source).toLowerCase()
  const formattedStyle = styleFullName || style.toUpperCase()

  useOnUnloadTimeOnPage()
  return (
    <>
      <SEOTitle
        title={`BibMe: Generate ${formattedStyle} ${formattedSource} citations for your bibliography`}
      />
      <MetaDescription
        content={`BibMe lets you easily and automatically create ${formattedSource} citations and build your bibliography in ${styleFullName}. It’s accurate and free!`}
      />
      <SourceSearch
        copy={{
          ...getSeoCopyWithSource(style, source),
          upgradePromotionCopy,
        }}
        styleId={getActualStyleId(style)}
        initialUrlStyleName={initialUrlStyleName}
        initialSourceType={source}
      />
    </>
  )
}

export const getServerSideProps = async ({ query }: NextPageContext) => {
  const { style, source: citeASource } = query as { style: string; source: string }
  const [initialUrlStyleName, styleFullName] = await getUrlStyleNameV2(style)
  const {
    params: { source },
  } = matchSourceSegment(citeASource)
  return {
    props: {
      styleFullName,
      initialUrlStyleName,
      isDefaultStyle: getActualStyleId(style) === Config.currentStyle,
      currentSourceType: source,
    },
    notFound:
      style === APA_ANNOTATED_BIBLIOGRAPHY ||
      isStyleIdInValid(style) ||
      !sourceTypeById(source),
  }
}

SourceSearchPage.getPageLayout = (page, ssrProps) =>
  getLvpLayout(page, () => <SourceSearchPageStructureTop {...ssrProps} />)

export default SourceSearchPage
